import React, { useCallback, useState, useMemo, useEffect } from "react"

import { useLocation, useHistory } from "react-router-dom"

import {
    Badge,
    Box,
    Tabs,
    Tab,
} from "@mui/material"

import {
    SupportAgent as SupportAgentIcon,
    Event as EventIcon,
    Groups as GroupsIcon,
    Apartment as ApartmentIcon,
    Campaign as CampaignIcon,
    ManageAccounts as ManageAccountsIcon,
    QueryStats as QueryStatsIcon,
    Phone as PhoneIcon
} from "@mui/icons-material"

import userPermissions from "../User/Permissions"
import Chat from "../Chat"
import Crm from "../Crm"
import ErrorBoundary from "../ErrorBoundary"
import ManageCalls from "../ManageCalls"
import ManageCallsOld from "../ManageCallsOld"
import Agenda from "../Agenda"
import ViewKanbanIcon from "@mui/icons-material/ViewKanban"
import AgentOverview from "../AgentOverview"
import Telephony from "../Telephony"
import { shouldUseNewWebphone } from "../Telephony/Utils"

import { getSession, clearSession, clearToken } from "../../auth"
import api from "../../api"
import useModuleAccess, { registerModuleAccess } from "../../utils/useModuleAccess"

import "./styles.css"

import CortexTabAssistant from "../CortexAssistant/CortexTabAssistant"
import Journeys from "../Journeys"
import CortexIcon from "../Cortex/icon"
import ContentUnavailable from "../ContentUnavailable"
import { useMenu } from "../../contexts/MenuContext"
import { hasActivePlatforms } from "../../utils/utils"


const groups = userPermissions()
const user = getSession()
const newManageCalls =
    user?.profile?.enterprise?.new_manage_calls === true

const components = {
    "supervision": ({ connections, availability, configurableUrls }) => ({
        label: "Supervisão",
        icon: <ManageAccountsIcon />,
        hidden: !groups.isSupervisor,

        isAvailable: () => availability.hasActivePlatforms,

        unavailable: {
            icon: <ManageAccountsIcon />,
            title: "Recurso indisponível para sua conta.",
            description: (
            	<a href={configurableUrls?.atendimento ? configurableUrls.atendimento : "https://solucoes.instant.com.br/kwik/"} target="_blank" rel="noopener noreferrer">
                    Para mais informações sobre o módulo de atendimento, clique aqui.
                </a>
            ),
        },

        component: (
            <ErrorBoundary
                componentName={
                    newManageCalls
                        ? "ManageCalls"
                        : "ManageCallsOld"
                }
                title="Ops"
                message="Algo deu errado, mas já fomos informados e estamos cuidando disso."
                showReset={true}
                logToBackend={true}
            >
                {newManageCalls
                    ? <ManageCalls />
                    : <ManageCallsOld />
                }
            </ErrorBoundary>
        )
    }),

    "service": ({ connections, availability, configurableUrls }) => ({
        id: "service",
        label: "Atendimento",
        icon: <SupportAgentIcon />,

        isAvailable: () => availability.hasActivePlatforms,

        unavailable: {
            icon: <SupportAgentIcon />,
            title: "Recurso indisponível para sua conta.",
            description: (
            	<a href={configurableUrls?.atendimento ? configurableUrls.atendimento : "https://solucoes.instant.com.br/kwik/"} target="_blank" rel="noopener noreferrer">
                    Para mais informações sobre o módulo de atendimento, clique aqui.
                </a>
            ),
        },

        component: <Chat connections={connections} />
    }),

    "agenda": ({ availability, configurableUrls }) => ({
        label: "Agenda",
        icon: <EventIcon />,

        isAvailable: () =>
            availability.user?.profile?.enterprise?.show_agenda === true,

        unavailable: {
            icon: <EventIcon />,
            title: "Recurso indisponível para sua conta.",
            description: (
            	<a href={configurableUrls?.agenda ? configurableUrls.agenda : "https://solucoes.instant.com.br/kwik/#agenda"} target="_blank" rel="noopener noreferrer">
                    Para mais informações sobre o módulo de agenda, clique aqui.
                </a>
            ),
        },

        component: <Agenda />
    }),

    "journeys": ({ availability, configurableUrls }) => ({
        label: "Jornadas",
        icon: <ViewKanbanIcon />,

        isAvailable: () =>
            availability.user?.profile?.enterprise?.show_journey === true,

        unavailable: {
            icon: <ViewKanbanIcon />,
            title: "Recurso indisponível para sua conta.",
            description: (
            	<a href={configurableUrls?.jornadas ? configurableUrls.jornadas : "https://solucoes.instant.com.br/kwik/#jornadas"} target="_blank" rel="noopener noreferrer">
                    Para mais informações sobre o módulo de jornadas, clique aqui.
                </a>
            ),
        },

        component: <Journeys />
    }),

    "contacts": () => ({
        label: "Contatos",
        icon: <GroupsIcon />,

        component: (
            <Crm
                key="crm-clients"
                selectedTab="clients"
            />
        )
    }),

    "enterprises": () => ({
        label: "Empresas",
        icon: <ApartmentIcon />,

        component: (
            <Crm
                key="crm-enterprises"
                selectedTab="enterprises"
            />
        )
    }),

    "campaigns": ({ availability, configurableUrls }) => ({
        label: "Campanhas",
        icon: <CampaignIcon />,

        isAvailable: () =>
            availability.user?.profile?.enterprise?.campaign_access === true,

        unavailable: {
            icon: <CampaignIcon />,
            title: "Recurso indisponível para sua conta.",
            description: (
                <a href={configurableUrls?.campanhas ? configurableUrls.campanhas : "https://solucoes.instant.com.br/kwik/#campanhas"} target="_blank" rel="noopener noreferrer">
                    Para mais informações sobre o módulo de campanhas, clique aqui.
                </a>
            ),
        },

        component: (
            <Crm
                key="crm-campaigns"
                selectedTab="campaigns"
            />
        )
    }),

    "cortex": ({ availability, configurableUrls }) => ({
        label: "Cortex",
        icon: <CortexIcon />,

        isAvailable: () =>
            availability.user?.profile?.enterprise?.has_assistant &&
            availability.user?.profile?.has_assistant,

        unavailable: {
            icon: <CortexIcon />,
            title: availability.user?.profile?.enterprise?.has_assistant !== true
                ? "Recurso indisponível para sua conta."
                : "Recurso indisponível para seu usuário.",
            description: availability.user?.profile?.enterprise?.has_assistant !== true
                ? (
                    <a href={configurableUrls?.cortex ? configurableUrls.cortex : "https://cortex.instant.com.br"} target="_blank" rel="noopener noreferrer">
                        Para mais informações sobre o Cortex, clique aqui.
                    </a>
                )
                : (
                    <span>
                        Entre em contato com seu administrador para ativar.
                    </span>
                )
        },

        component: <CortexTabAssistant />
    }),

    "overview": ({ availability, configurableUrls }) => ({
        label: "Visão Geral",
        icon: <QueryStatsIcon />,

        isAvailable: () => shouldUseNewWebphone(availability.user),

        unavailable: {
            icon: <QueryStatsIcon />,
            title: "Recurso indisponível para sua conta.",
            description: (
            	<a href={configurableUrls?.telefonia ? configurableUrls.telefonia : "https://landing.instant.com.br/instant-cloud-voice/"} target="_blank" rel="noopener noreferrer">
                    Para mais informações sobre o módulo de telefonia, clique aqui.
                </a>
            ),
        },

        component: <AgentOverview />
    }),

    "telephony": ({ availability, configurableUrls }) => ({
        label: "Telefonia",
        icon: <PhoneIcon />,

        isAvailable: () => shouldUseNewWebphone(availability.user),

        unavailable: {
            icon: <PhoneIcon />,
            title: "Recurso indisponível para sua conta.",
            description: (
            	<a href={configurableUrls?.telefonia ? configurableUrls.telefonia : "https://landing.instant.com.br/instant-cloud-voice/"} target="_blank" rel="noopener noreferrer">
                    Para mais informações sobre o módulo de telefonia, clique aqui.
                </a>
            ),
        },

        component: <Telephony />
    }),
}


const generateComponents = (kwargs) => {
    const newComponents = {}

    Object.entries(components).forEach(([key, func]) => {
        const tab = func(kwargs)

        if (tab.hidden)
            return

        newComponents[key] = tab
    })

    return newComponents
}


const aliasMap = {
    "notifications": "campaigns"
}


const Main = ({ connections, hasNewMessage = false }) => {
    const { configurableUrls } = useMenu()

    const location = useLocation()
    const history = useHistory()

    const currentUser = getSession()

    const availability = useMemo(() => ({
        user: currentUser,

        platforms:
            currentUser?.profile?.enterprise?.platforms || {},

        hasActivePlatforms: hasActivePlatforms(
            currentUser?.profile?.enterprise?.platforms || {}
        ),
    }), [])

    const exec_components = useMemo(
        () => generateComponents({
            connections,
            availability,
            configurableUrls,
        }),
        [connections, availability, configurableUrls]
    )

    const tab_entries = useMemo(
        () => Object.entries(exec_components),
        [exec_components]
    )

    const getSelectedTab = () => {
        const searchParams = new URLSearchParams(location.search)

        let selectedTab = searchParams.get("selectedTab")

        selectedTab = aliasMap[selectedTab] || selectedTab

        return tab_entries.some(([key]) => key === selectedTab)
            ? selectedTab
            : tab_entries.find(([, tab]) => !tab.hidden)?.[0]
    }

    const [activeTab, setActiveTab] = useState(getSelectedTab)
    const accessModules = {
        service: "attendance", journeys: "journeys", campaigns: "campaigns",
        agenda: "agenda", contacts: "contacts", enterprises: "crm_enterprises",
        supervision: "supervision", cortex: "cortex",
    }
    const accessTab = exec_components[activeTab]
    useModuleAccess(accessTab && !accessTab.hidden
        ? accessModules[activeTab] : null)

    const checkOnline = useCallback(async () => {
        const user = getSession()

        const res = await api.get(
            `/api/enterprises/${user.profile.enterprise.id}/users/${user.id}/useronline/`
        )

        if (res.status === 200) {
            if (res.data === "LOGOFF") {
                clearSession()
                clearToken()

                document.location.href =
                    "/?typeAlert=error&showAlert=Usuário deslogado externamente."
            }

            return
        }
    }, [])

    useEffect(() => {
        setActiveTab(getSelectedTab())

        checkOnline()

        const intervalId = setInterval(
            () => checkOnline(),
            60000
        )

        return () => clearInterval(intervalId)
    }, [location.search, checkOnline])

    const renderActiveComponent = () => {
        const tab = exec_components[activeTab]

        if (!tab)
            return null

        if (
            tab.isAvailable &&
            !tab.isAvailable()
        ) {
            const unavailable = tab.unavailable || {}

            return (
                <ContentUnavailable
                    icon={
                        unavailable.icon ||
                        tab.icon
                    }
                    title={
                        unavailable.title ||
                        "Conteúdo indisponível."
                    }
                    description={
                        unavailable.description
                    }
                />
            )
        }

        return tab.component
    }

    return (
        <Box className="main-container">

            <Tabs
                className="main-container-tabs"
                variant="scrollable"
                scrollButtons="auto"
                value={activeTab}
            >
                {tab_entries.map(([key, tab]) => (
                    <Tab
                        className={
                            activeTab === key
                                ? "active"
                                : ""
                        }
                        onClick={() => {
                            if (key === activeTab)
                                registerModuleAccess(accessModules[key])
                            setActiveTab(key)

                            const urlParams =
                                new URLSearchParams()

                            urlParams.set(
                                "selectedTab",
                                key
                            )

                            history.push(
                                `${location.pathname}?${urlParams.toString()}`
                            )
                        }}
                        value={key}
                        icon={
                            tab.label === "Atendimento" &&
                            hasNewMessage
                                ? (
                                    <Badge
                                        color="success"
                                        variant="dot"
                                        anchorOrigin={{
                                            vertical: "top",
                                            horizontal: "right",
                                        }}
                                    >
                                        {tab.icon}
                                    </Badge>
                                )
                                : tab.icon
                        }
                        label={tab.label}
                        iconPosition="start"
                        key={key}
                    />
                ))}
            </Tabs>

            <Box className="main-container-content">
                {renderActiveComponent()}
            </Box>

        </Box>
    )
}

export default Main